import { formatBytes, RunStatus } from "@kaggle/components";
import { Run } from "../../../../types/entities/run";
import { RunActionKey } from "../../../actions/ActionKeys";
import {
  FetchRunSuccessAction,
  RunsAction,
  SetDatasetDownloadProgressMsgAction,
  SetRunStatusAction,
  SetRunStatusBatchedAction,
  UpdateResourceUsageAction,
  UpdateRunLogsAction,
  UpdateStdErrAction,
  UpdateStdOutAction,
} from "../../../actions/runs";

export interface State {
  [id: number]: Run;
}

const QUEUED_RUN_STATUS: RunStatus = RunStatus.QUEUED;

const mergeRun = (
  id: number,
  prevState: Partial<Run> = {},
  newState: Partial<Run> = {}
): Run => {
  return {
    status: QUEUED_RUN_STATUS,
    resourceUsage: null,
    logs: null,
    stdOut: [],
    stdErr: [],
    datasetDownloadProgressMessages: [],
    ...prevState,
    ...newState,
    id,
    dateCreated: prevState.dateCreated || newState.dateCreated,
  };
};

/** Case reducer to add new run. */
const addRun = (state: State, action: FetchRunSuccessAction): State => {
  const { id } = action.payload;
  return {
    ...state,
    [id]: mergeRun(id, state[id]),
  };
};

/** Case reducer to update status of a run. */
const updateStatus = (state: State, action: SetRunStatusAction): State => {
  const { id, status } = action.payload;
  return {
    ...state,
    [id]: mergeRun(id, state[id], { status }),
  };
};

/** Case reducer to update status of an array of runs. */
const updateStatusBatched = (
  state: State,
  action: SetRunStatusBatchedAction
): State => {
  const newState = {
    ...state,
  };

  for (const run of action.payload) {
    const { id, status, exitCode, dateCreated } = run;
    newState[id] = mergeRun(id, state[id], {
      status,
      exitCode,
      dateCreated,
    });
  }

  return newState;
};

/** Case reducer to update status of a run. */
const updateResourceUsage = (
  state: State,
  action: UpdateResourceUsageAction
): State => {
  const { id, resourceUsage } = action.payload;
  return {
    ...state,
    [id]: mergeRun(id, state[id], { resourceUsage }),
  };
};

const updateRunLogs = (state: State, action: UpdateRunLogsAction): State => {
  const { id, logs } = action.payload;
  return {
    ...state,
    [id]: mergeRun(id, state[id], { logs }),
  };
};

const updateRunStdErr = (state: State, action: UpdateStdErrAction): State => {
  const { id, stdErr } = action.payload;
  return {
    ...state,
    [id]: mergeRun(id, state[id], { stdErr: state[id].stdErr.concat(stdErr) }),
  };
};

const updateRunStdOut = (state: State, action: UpdateStdOutAction): State => {
  const { id, stdOut } = action.payload;
  return {
    ...state,
    [id]: mergeRun(id, state[id], { stdOut: state[id].stdOut.concat(stdOut) }),
  };
};

const updateRunDatasetDownloadMsg = (
  state: State,
  action: SetDatasetDownloadProgressMsgAction
): State => {
  const { runId, messages } = action.payload;
  // TODO: May be able to remove filter statement.
  return {
    ...state,
    [runId]: mergeRun(runId, state[runId], {
      datasetDownloadProgressMessages: messages.filter(
        message => !!message.length
      ),
    }),
  };
};

/** Reducer used to manage kernel runs. */
export const runs = (state: State = {}, action: RunsAction): State => {
  switch (action.type) {
    case RunActionKey.SET_RUN_STATUS:
      return updateStatus(state, action);
    case RunActionKey.SET_RUN_STATUS_BATCHED:
      return updateStatusBatched(state, action);
    case RunActionKey.FETCH_RUN_SUCCESS:
      return addRun(state, action);
    case RunActionKey.UPDATE_RESOURCE_USAGE:
      return updateResourceUsage(state, action);
    case RunActionKey.UPDATE_RUN_LOGS:
      return updateRunLogs(state, action);
    case RunActionKey.UPDATE_STD_OUT:
      return updateRunStdOut(state, action);
    case RunActionKey.UPDATE_STD_ERR:
      return updateRunStdErr(state, action);
    case RunActionKey.SET_DATASET_DOWNLOAD_PROGRESS_MSG:
      return updateRunDatasetDownloadMsg(state, action);
    default:
      return state;
  }
};

export const isDatasetAttaching = (state: State, id: number | null) => {
  if (id === null) {
    return;
  }

  const messages = mergeRun(id, state[id]).datasetDownloadProgressMessages;
  return messages && messages.length > 0;
};

export const getResourceUsage = (state: State, id: number | null) => {
  if (id === null) {
    return;
  }

  return mergeRun(id, state[id]).resourceUsage;
};

/**
 * Returns formatted string to describe usage over limit.
 *
 * @param usage Usage in bytes
 * @param limit Limit in bytes
 */
export const formatUsageLimitText = (usage: number, limit: number) =>
  `${formatBytes(usage, 1)}/${formatBytes(limit, 1)}`;

/**
 * Returns batched session based on available run.
 *
 * @param state Runs entity reducer state.
 * @param id Run id.
 */
export const getBatchedSession = (state: State, id: number | null) => {
  if (id === null) {
    return null;
  }

  if (id) {
    let batchedCpu;
    let batchedDisk;
    let batchedRam;

    const batchedRun = state[id];
    if (batchedRun && batchedRun.resourceUsage) {
      batchedCpu = `${batchedRun.resourceUsage.cpu.pct.toFixed(2)}%`;
      batchedDisk = formatUsageLimitText(
        batchedRun.resourceUsage.disk.usage,
        batchedRun.resourceUsage.disk.limit
      );
      batchedRam = formatUsageLimitText(
        batchedRun.resourceUsage.ram.usage,
        batchedRun.resourceUsage.ram.limit
      );
    }

    return {
      status: batchedRun.status,
      resourceUsageAvailable: batchedRun.resourceUsage !== null,
      cpu: batchedCpu,
      disk: batchedDisk,
      ram: batchedRam,
    };
  }
  return null;
};
